import React, { useState, useEffect } from 'react'
import { ActionButton } from '@emerald-works-nova/components'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { Box, Divider, Grid, Switch, Typography, useTheme } from '@mui/material'
import { useDispatch } from 'react-redux'
import { BannerStatic } from '../../../components'
import { TenantEditSlice } from '../../../reducers'
import { useStyles } from '../features-tab/style'
import { useTenantFormContext } from '../tenant-form-context'
import { Controller } from 'react-hook-form'

const MobileAppSettings = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const dispatch = useDispatch()
  const { tenant, isEdit, form } = useTenantFormContext()
  const { watch, setValue } = form
  const [isLoading, setIsLoading] = useState(false)
  const hasContentHub = watch('features.contentHub') || tenant?.features?.contentHub
  const mobileApp = watch('features.mobileApp')

  const handleSwitch = () => {
    setValue('features.mobileApp', !mobileApp)
  }

  useEffect(() => {
    if (!isEdit) {
      setValue('features.mobileApp', false)
      return
    }
    if (!hasContentHub) {
      setValue('features.mobileApp', false)
      return
    }
    const hasMobileApp = tenant?.features?.mobileApp
    setValue('features.mobileApp', hasMobileApp)
  }, [tenant, isEdit, setValue, hasContentHub])

  const [tenantUpdate] = useEvent([
    {
      eventName: 'tenantUpdate',
      onSuccess: (tenant) => {
        BannerStatic.show({ label: 'Mindtools App configuration updated' })
        setIsLoading(false)
        dispatch(TenantEditSlice.actions.updateTenantFeatures(tenant.features))
      },
      onError: () => {
        BannerStatic.show({
          label: 'There was an error saving the Mindtools App feature configuration',
          isError: true
        })
        setIsLoading(false)
      }
    }
  ])

  const handleSave = () => {
    const features = {
      ...tenant.features,
      mobileApp: mobileApp
    }
    if (mobileApp) {
      features.mindsetAi = true
    }

    tenantUpdate.trigger({
      pk: tenant.pk,
      features
    })
    setIsLoading(true)
  }

  return (
    <Grid container className={classes.featContainer}>
      <Grid item xs={12}>
        <Box
          border={1}
          borderRadius='undefinedpx'
          borderColor='#E0E0E0'
          display='flex'
          style={{
            justifyContent: 'space-between',
            flexDirection: 'column',
            padding: spacing(1, 4)
          }}
        >
          <Box display='flex' flexDirection='row'>
            <Box display='flex' flexDirection='column'>
              <Typography variant='body2' style={{ marginTop: spacing(3.5), marginBottom: spacing(3.5) }}>
                Mindtools App
              </Typography>
              <Divider style={{ marginBottom: spacing(3.5) }} />
              <Typography variant='body2' color='textSecondary' style={{ display: 'flex', flex: 1, marginRight: '12px', marginBottom: spacing(1) }}>
                Enable this feature for clients who should have access to the mobile version of the Mindtools App (M: Coach).
              </Typography>
              <Typography variant='body2' color='textSecondary' style={{ display: 'flex', flex: 1, marginRight: '12px', marginBottom: spacing(3.5) }}>
                Please note: both Content Hub and M: Coach must also be enabled for this to work properly. This setting can be turned off at any time.
              </Typography>
            </Box>
            <Box
              display='flex'
              style={{
                justifyContent: 'flex-end',
                alignItems: 'flex-start',
                flex: 1,
                padding: spacing(1)
              }}
            >
              <Box style={{ padding: spacing(2, 0.5) }}>
                <Controller
                  name='features.mobileApp'
                  control={form.control}
                  render={({ field }) =>
                    <Switch
                      color='primary'
                      checked={!!field.value}
                      onChange={handleSwitch}
                      disabled={isLoading || !hasContentHub}
                      data-test='mobile-app-product-toggle'
                      {...field}
                    />}
                />
              </Box>
            </Box>
          </Box>

          {isEdit && (
            <Box>
              <Divider style={{ marginBottom: spacing(1) }} />

              <Box display='flex' justifyContent='flex-end' mt={1} mb={1}>
                <ActionButton
                  onClick={handleSave}
                  disabled={isLoading || !hasContentHub}
                  variant='primary'
                  dataTest='mobile-app-save-button'
                  style={{ height: 'min-content' }}
                > Save
                </ActionButton>
              </Box>
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  )
}

export default MobileAppSettings
